import { useState, useContext, useEffect, useRef } from 'react';
import type { JSX, MouseEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
// contexts
import { ProjectContext } from '../../contexts/ProjectContext';
import { ValidationContext } from '../../contexts/ValidationContext';
import { CurrentProjectContext } from 'contexts/CurrentProjectContext';
import { Animate, ViewContext, AuthContext } from 'components/lib';
import type { ID, ProjectOwner } from 'types/project';

// Material UI
import IconButton from '@mui/material/IconButton';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { ValidationMinMaxContext } from 'contexts/ValidationMinMaxContext';
// import { changeFormatOfDate } from "utils/changeFormatOfDate";
import { DeleteIcon, VerticalDotsIcon, InfoIcon, SwitchUserIcon } from 'components/icons';
import EditableText from 'components/editable-text/EditableText';
import {
  deleteProject,
  getProjectUsers,
  getProjects,
  setProjectSupportFlag,
  updateProject,
} from '../../api/projectApi';
import { openSupportCountsTowardUsageDialog, storePendingCountsTowardUsage } from '../../services/countsTowardUsage';
import { UMButton } from '@urbanmetrix/um-kit';
import { useDashboardGuidedTour } from './GuidedTour';
import { getCurrentUsage } from 'api/planApi';
import { exceedsVolumeLimit } from 'services/gridLimitChecks';
import { isStorageLimitReached } from 'services/storageLimit';
import { formatBytes } from '../../utils/formatBytes';

type DashboardProject = {
  id: ID;
  name: string;
  type?: string;
  createdAt?: string | Date;
  deletedAt?: string | Date | null;
  size?: number;
  owner?: ProjectOwner | null;
  paidVolume?: number | null;
  isLocked?: boolean;
  [key: string]: unknown;
};

type ProjectUser = {
  id: ID;
  name?: string;
  email?: string;
  isProjectOwner?: boolean;
};

const SupportFlagIcon = ({ flagged }: { flagged: boolean }) => (
  <img
    src={flagged ? '/svg/support-flag-filled.svg' : '/svg/support-flag.svg'}
    alt={flagged ? 'Support flagged' : 'Not support flagged'}
    width={18}
    height={18}
    style={{ display: 'block', cursor: 'pointer' }}
  />
);

export function Dashboard(): JSX.Element {
  const authContext = useContext(AuthContext);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Actions Detail
  const [selectedProject, setSelectedProject] = useState<DashboardProject | null>(null);
  const [anchorElActionsDetail, setAnchorElActionsDetail] = useState<HTMLElement | null>(null);
  const openActionsDetail = Boolean(anchorElActionsDetail);

  const handleClickActionsDetail = (event: MouseEvent<HTMLElement>, project: DashboardProject) => {
    setAnchorElActionsDetail(event.currentTarget);
    setSelectedProject(project);
  };

  const handleCloseActionsDetail = () => {
    setAnchorElActionsDetail(null);
  };

  const handleMenuCloseActionsDetail = (_event: {}, _reason: 'backdropClick' | 'escapeKeyDown') => {
    handleCloseActionsDetail();
  };

  const { clearProjectFormData } = useContext(ProjectContext);
  const { setCurrentProject } = useContext(CurrentProjectContext);
  const context = useContext(ViewContext);
  const { clearValidationForProjectName, clearValidationForProjectMunicipality, clearProjectValidationErrors } =
    useContext(ValidationContext);
  const { clearValidationProjectMinMaxInputs } = useContext(ValidationMinMaxContext);
  const activeOrganizationId = authContext.user?.session?.activeOrganizationId;
  const hasOrganization = Boolean(activeOrganizationId) && !authContext.user?.requiresOrganizationSetup;
  const hasActiveSubscription = hasOrganization && authContext.user?.plan === true;
  const currentUserId = authContext.user?.id;
  const canSeeDeletedProjects = ['dev', 'support'].includes(authContext.user?.appRole ?? '');
  const canBypassProjectLocks = ['admin', 'dev', 'support'].includes(authContext.user?.appRole ?? '');
  const showDeletedProjectsStorageKey = currentUserId ? `dashboard.showDeletedProjects.${currentUserId}` : null;
  const showSizesStorageKey = currentUserId ? `dashboard.showSizes.${currentUserId}` : null;

  const [projects, setProjects] = useState<DashboardProject[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [showDeletedProjects, setShowDeletedProjects] = useState(false);
  const [showSizes, setShowSizes] = useState<boolean | null>(null);
  const [storageLimitReached, setStorageLimitReached] = useState(false);
  const hasShownProjectsLoadErrorRef = useRef(false);

  useEffect(() => {
    setShowDeletedProjects(
      canSeeDeletedProjects && showDeletedProjectsStorageKey
        ? localStorage.getItem(showDeletedProjectsStorageKey) === 'true'
        : false
    );
  }, [canSeeDeletedProjects, showDeletedProjectsStorageKey]);

  useEffect(() => {
    setShowSizes(Boolean(showSizesStorageKey && localStorage.getItem(showSizesStorageKey) === 'true'));
  }, [showSizesStorageKey]);

  const visibleProjects = projects.filter((project) => showDeletedProjects || !project.deletedAt);
  const hasProjects = visibleProjects.length > 0;

  useEffect(() => {
    if (showSizes === null) return;

    if (!hasActiveSubscription) {
      setProjects([]);
      setStorageLimitReached(false);
      setIsLoadingProjects(false);
      hasShownProjectsLoadErrorRef.current = false;
      return;
    }

    let cancelled = false;
    const includeSizes = showSizes;

    async function loadProjects() {
      try {
        const [data, usage] = await Promise.all([getProjects(includeSizes), getCurrentUsage()]);
        if (cancelled) return;

        setStorageLimitReached(isStorageLimitReached(usage));
        setProjects(
          (data || []).map((project) => ({
            ...project,
            isLocked:
              !canBypassProjectLocks &&
              exceedsVolumeLimit((project as DashboardProject).paidVolume, usage?.volumeLimit),
          }))
        );
        hasShownProjectsLoadErrorRef.current = false;
      } catch (error) {
        if (cancelled) return;

        setProjects([]);
        setStorageLimitReached(false);
        if (!hasShownProjectsLoadErrorRef.current) {
          hasShownProjectsLoadErrorRef.current = true;
          context.notification.show('Unable to load projects', 'error', true, 'toast');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingProjects(false);
        }
      }
    }

    loadProjects();

    return () => {
      cancelled = true;
    };
  }, [activeOrganizationId, canBypassProjectLocks, hasActiveSubscription, showSizes]);

  // Clear all data and set next project name using already-fetched list to avoid extra calls
  useEffect(() => {
    clearProjectFormData(projects.length);
    clearValidationProjectMinMaxInputs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects.length]);

  // Leaving a project: clear currentProject to avoid stale state/polling when on dashboard
  useEffect(() => {
    setCurrentProject(null);
  }, [setCurrentProject]);

  /**
   * Delete item from tree table
   */
  const handleDeleteItem = (pProject: DashboardProject) => {
    context.modal.show(
      {
        title: `Delete Project`,
        form: {},
        buttonText: 'Delete',
        text: `Are you sure you want to delete ${pProject.name}?`,
        url: '',
        method: 'DELETE',
        customSubmit: () => deleteProject(String(pProject.id)),
        destructive: true,
      },
      () => {
        setProjects((prevProjects) =>
          canSeeDeletedProjects
            ? prevProjects.map((project) =>
                project.id === pProject.id ? { ...project, deletedAt: new Date().toISOString() } : project
              )
            : prevProjects.filter((project) => project.id !== pProject.id)
        );
        context.notification.show(`${pProject.name} was deleted`, 'success', true, 'toast');
      }
    );
  };

  const handleAddProject = async (): Promise<boolean> => {
    clearValidationForProjectName();
    clearValidationForProjectMunicipality();
    clearProjectValidationErrors();
    return true;
  };

  const handleStartProjectCreation = async () => {
    const canProceed = await handleAddProject();
    if (!canProceed) {
      return;
    }

    if (authContext.permission?.support) {
      openSupportCountsTowardUsageDialog({
        context,
        entityLabel: 'project',
        defaultCountsTowardUsage: true,
        onContinue: (countsTowardUsage: boolean) => {
          storePendingCountsTowardUsage(countsTowardUsage);
          navigate('/projects/add');
        },
      });
      return;
    }

    navigate('/projects/add');
  };

  const showStorageInfo = () => {
    context.modal.show({
      title: 'Storage limit reached',
      maxWidth: 520,
      customHtml: (
        <>
          <p>Your storage is full. You can:</p>
          <ul style={{ listStyle: 'disc', paddingLeft: '24px', margin: '12px 0 20px' }}>
            <li style={{ marginBottom: '10px' }}>
              <a href="/account/change-plan">Upgrade your plan</a> to get more storage space.
            </li>
            <li>Delete data. Enable “Show sizes” to identify which projects use the most storage.</li>
          </ul>
        </>
      ),
    });
  };

  const handleChangeOwner = async (project: DashboardProject) => {
    try {
      const users: ProjectUser[] = await getProjectUsers(String(project.id));
      const options = users
        .filter((user) => !user.isProjectOwner)
        .map((user) => ({
          value: user.id,
          label: user.email ? `${user.name ?? user.email} (${user.email})` : `${user.name ?? user.id}`,
        }));

      const hasOptions = options.length > 0;

      if (!hasOptions) {
        context.modal.show({
          title: `Change owner`,
          text: `There are no other users available for ${project.name}.`,
        });
        return;
      }

      context.modal.show(
        {
          title: `Change owner`,
          text: `Select the new owner for ${project.name}.`,
          form: {
            ownerId: {
              label: 'New owner',
              type: 'select',
              required: true,
              value: '',
              options,
            },
          },
          buttonText: 'Change owner',
          customSubmit: (data: { ownerId?: ID }) =>
            updateProject(String(project.id), { ownerId: String(data.ownerId) }),
        },
        (_form?: unknown, rawRes?: unknown) => {
          const res = rawRes as { id?: ID; owner?: ProjectOwner | null; ownerId?: ID } | null | undefined;
          if (!res?.id) return;
          const nextOwner = users.find((user) => res.ownerId && String(user.id) === String(res.ownerId));
          setProjects((prevProjects) =>
            prevProjects.map((item) =>
              String(item.id) === String(res.id)
                ? {
                    ...item,
                    owner: res.owner ?? (res.ownerId ? { userId: res.ownerId, name: nextOwner?.name } : item.owner),
                  }
                : item
            )
          );
        }
      );
    } catch (error) {
      context.notification.show('Unable to load project users', 'error', true, 'toast');
    }
  };

  // Function to handle project name update
  const updateProjectName = async (id: ID, newName: string) => {
    try {
      await updateProject(String(id), { name: newName });
      setProjects((prevProjects) =>
        prevProjects.map((p) => (String(p.id) === String(id) ? { ...p, name: newName } : p))
      );
      context.notification.show(`Project name has been updated`, 'success', true, 'toast');
    } catch (error) {
      context.notification.show('Error updating project name', 'error', true, 'toast');
    }
  };

  const canManageProject = (project: DashboardProject | null) =>
    Boolean(
      authContext.permission?.support ||
      authContext.permission?.admin ||
      (project?.owner?.userId && currentUserId && String(project.owner.userId) === String(currentUserId))
    );
  const canManageSelectedProject = canManageProject(selectedProject);
  const guidedTourEnabled = searchParams.get('guidedTour') === 'true';
  useDashboardGuidedTour({ enabled: guidedTourEnabled, setSearchParams });

  let welcomeMessage: { description: JSX.Element; action?: JSX.Element } | null = null;
  if (!hasOrganization) {
    welcomeMessage = {
      description: (
        <>
          To use UrbanMetrix, you need to be part of an organization. If you&apos;re the owner of your organization, you
          can create a new one from the top panel.
          <br />
          Otherwise, ask your organization owner to invite you to an existing organization.
        </>
      ),
    };
  } else if (!hasActiveSubscription && authContext.permission?.owner) {
    welcomeMessage = {
      description: <>To use UrbanMetrix, you need to subscribe to a plan for your organization.</>,
      action: (
        <Link className="dashboard-welcome-message__cta" to="/register/plan">
          View plans
        </Link>
      ),
    };
  } else if (!hasActiveSubscription) {
    welcomeMessage = {
      description: <>Your organization needs an active subscription. Please ask its owner to subscribe to a plan.</>,
    };
  }

  const projectsTemplate = visibleProjects
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a.createdAt ?? 0);
      const dateB = new Date(b.createdAt ?? 0);

      return dateB.getTime() - dateA.getTime();
    })
    .map((project) => {
      const isLocked = Boolean(project.isLocked);
      const hasProjectActions = canManageProject(project);
      const canToggleSupportFlag = Boolean(authContext.permission?.editor);
      const canViewSupportFlag = canToggleSupportFlag || Boolean(authContext.permission?.support);

      return (
        <TableRow
          key={project.id}
          hover={!isLocked}
          onClick={(event) => {
            if (openActionsDetail) {
              event.stopPropagation();
              handleCloseActionsDetail();
              return;
            }
            if (isLocked) return;
            navigate(`/projects/${project.id}`);
          }}
          className={isLocked ? undefined : 'row-clickable'}
          sx={{
            cursor: isLocked ? 'default' : 'pointer',
            color: project.deletedAt || isLocked ? 'text.disabled' : undefined,
            '& td': {
              cursor: 'inherit',
              color: project.deletedAt || isLocked ? 'text.disabled' : undefined,
            },
          }}
        >
          <TableCell sx={{ width: '40%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {isLocked && (
                <Tooltip title="This project exceeds your current plan's volume limit. Upgrade your subscription to regain access.">
                  <LockOutlinedIcon aria-label="Project locked" fontSize="small" />
                </Tooltip>
              )}
              {isLocked ? (
                <span>{project.name}</span>
              ) : (
                <EditableText
                  text={project.name}
                  onUpdate={(newName: string) => updateProjectName(project.id, newName)}
                  navigateTo={() => navigate(`/projects/${project.id}`)}
                  stopPropagationOnClick
                />
              )}
            </div>
          </TableCell>

          <TableCell align="center" sx={{ width: '20%' }}>
            <div className="center-cell">
              {!isLocked && (
                <InfoIcon
                  onClick={(event: any) => {
                    event.stopPropagation();
                    navigate(`/projects/${project.id}/review`);
                  }}
                ></InfoIcon>
              )}
            </div>
          </TableCell>
          {showSizes && (
            <TableCell align="center" sx={{ width: '20%' }}>
              {formatBytes(project.size)}
            </TableCell>
          )}
          <TableCell align="center" sx={{ width: '20%' }}>
            <div className="center-cell">
              <>
                <span
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <IconButton
                    disabled={!hasProjectActions}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClickActionsDetail(event, project);
                    }}
                  >
                    <VerticalDotsIcon />
                  </IconButton>
                </span>
                <Menu
                  anchorEl={anchorElActionsDetail}
                  open={openActionsDetail}
                  onClose={handleMenuCloseActionsDetail}
                  className="actions-menu"
                >
                  {canViewSupportFlag && (
                    <MenuItem
                      onClick={async (event) => {
                        event.stopPropagation();
                        if (!canToggleSupportFlag || !selectedProject) return;
                        const updated = await setProjectSupportFlag(
                          String(selectedProject.id),
                          !Boolean(selectedProject.supportFlagged)
                        );
                        setProjects((current) =>
                          current.map((currentProject) =>
                            currentProject.id === selectedProject.id
                              ? { ...currentProject, ...updated }
                              : currentProject
                          )
                        );
                        setSelectedProject((current) => (current ? { ...current, ...updated } : current));
                      }}
                      className="actions-menu-item"
                    >
                      <Tooltip title="Flag for support">
                        <IconButton aria-label="Flag for support">
                          <SupportFlagIcon flagged={Boolean(selectedProject?.supportFlagged)} />
                        </IconButton>
                      </Tooltip>
                    </MenuItem>
                  )}
                  {canManageSelectedProject && !selectedProject?.isLocked && (
                    <MenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        if (selectedProject) handleChangeOwner(selectedProject);
                        handleCloseActionsDetail();
                      }}
                      className="actions-menu-item"
                    >
                      <Tooltip title={`Change owner for ${selectedProject?.name}`}>
                        <IconButton aria-label="change owner">
                          <SwitchUserIcon />
                        </IconButton>
                      </Tooltip>
                    </MenuItem>
                  )}
                  {canManageSelectedProject && (
                    <MenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        if (selectedProject) handleDeleteItem(selectedProject);
                        handleCloseActionsDetail();
                      }}
                    >
                      <Tooltip title={`Delete ${selectedProject?.name}`}>
                        <IconButton aria-label="delete" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </MenuItem>
                  )}
                </Menu>
              </>
            </div>
          </TableCell>
        </TableRow>
      );
    });

  return (
    <Animate type="pop">
      <div id="projects-page">
        {welcomeMessage ? (
          <section className="dashboard-welcome-message">
            <h1>Welcome to</h1>
            <img
              className="dashboard-welcome-message__logo"
              src="/images/logos/LOGO%20URBANMETRIX_URBANMETRIX_LOGO_POS_CLEAR.png"
              alt="UrbanMetrix"
            />
            <p>{welcomeMessage.description}</p>
            {welcomeMessage.action}
          </section>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '0.65rem',
                marginRight: '64px',
              }}
            >
              {canSeeDeletedProjects && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showDeletedProjects}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setShowDeletedProjects(checked);
                        if (showDeletedProjectsStorageKey) {
                          localStorage.setItem(showDeletedProjectsStorageKey, String(checked));
                        }
                      }}
                    />
                  }
                  label="Show deleted items"
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showSizes === true}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setShowSizes(checked);
                      if (showSizesStorageKey) localStorage.setItem(showSizesStorageKey, String(checked));
                    }}
                  />
                }
                label="Show sizes"
              />
              {authContext.permission?.admin && (
                <div data-guided-tour="guided-tour-add-project" className="guided-tour-add-project-target">
                  <UMButton text="Add Project" onClick={handleStartProjectCreation} />
                </div>
              )}
            </div>
            <br />
            {storageLimitReached && (
              <Alert
                severity="warning"
                sx={{ alignItems: 'center', marginBottom: '16px', width: 'fit-content', maxWidth: '100%' }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                  <span>Storage limit reached.</span>
                  <button
                    type="button"
                    onClick={showStorageInfo}
                    style={{
                      border: 0,
                      padding: 0,
                      background: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      font: 'inherit',
                      fontWeight: 700,
                      textDecoration: 'underline',
                    }}
                  >
                    More
                  </button>
                </div>
              </Alert>
            )}
            {isLoadingProjects ? (
              <Alert sx={{ alignItems: 'center' }} severity="info" className="stat" icon={false}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span>Loading projects...</span>
                </div>
              </Alert>
            ) : !hasProjects ? (
              <Alert sx={{ alignItems: 'center' }} severity="info" className="stat">
                No project found!
                {!authContext.permission?.admin && (
                  <div>
                    Ask to your account owner to provide you with editing access to some existing projects, or to change
                    your permissions from editor to admin to be able to create new projects.
                  </div>
                )}
              </Alert>
            ) : (
              <>
                <TableContainer component={Paper} className="custom-table-container">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: '40%' }}>Name</TableCell>
                        <TableCell align="center" sx={{ width: '20%' }}>
                          Info
                        </TableCell>
                        {showSizes && (
                          <TableCell align="center" sx={{ width: '20%' }}>
                            Size
                          </TableCell>
                        )}
                        <TableCell align="center" sx={{ width: '20%' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>{projectsTemplate}</TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </div>

      {/* <Feedback /> */}
    </Animate>
  );
}
